import React, { memo, useState } from 'react';
import {isUserAdmin, isUserSuperAdmin } from '../../stores/business/userBusiness';
import practiceBusiness from '../../stores/business/practiceBusiness';
import roleLevelBusiness from '../../stores/business/roleLevelBusiness';
import { useDispatch, useSelector } from 'react-redux';
import { addEmployee, updateEmployee } from '../../stores/business/manageEmployeeBusiness';
import { ENewEmployeeKey, ModalType, generalPracticesNames } from './enum';
import { formatDate } from '../../lib/dateTimesHelpers';
import { modalCreateUpdateEmployeeStateSelector } from '../../stores/selectors/manageEmployeeStateSelector';
import { hideModal, setPractice, setRoleLevels } from '../../stores/actions/manageEmployeeAction';
import { ERoleId } from '../../const/dataEnum';
import EssModal from '../../components/EssModal/EssModal';
import { FmlxButton } from 'fmlx-common-ui';
import EssFormContent from '../../components/EssForm/EssFormContent';
import { selectDepartments } from '../../stores/selectors/departmentSelector';

const ModalCreateUpdateEmployee = () => {
  const {
    departments,
    companies,
    authRoles,
    roleLevels,
    practices,
    show,
    modalType,
    newEmployee,
    userAuthRoleId,
    userDeptId,
  } = useSelector(modalCreateUpdateEmployeeStateSelector);
  const employeeId = newEmployee?.id;
  const [state, setState] = useState(null);
  const [loadingModify, setLoadingModify] = useState(false);
  const [prevEmployeeId, setPrevEmployeeId] = useState(employeeId);
  const [prevModalType, setPrevModalType] = useState(modalType);
  const isSuperAdmin = isUserSuperAdmin(userAuthRoleId);
  const isAdmin = isUserAdmin(userAuthRoleId);
  const defaultId = -9999;

  const dispatch = useDispatch();

  if (!state || prevEmployeeId !== employeeId || modalType !== prevModalType) {
    setPrevModalType(modalType);
    setPrevEmployeeId(employeeId);

    switch (modalType) {
      case ModalType.UPDATE: {
        setState({
          id: newEmployee?.id,
          fullName: newEmployee?.fullName ?? "",
          authRoleID: newEmployee?.authRoleID,
          userName: newEmployee?.userName ?? "",
          departmentID: newEmployee?.departmentID,
          practiceID: newEmployee?.practiceID,
          roleLevelID: newEmployee?.roleLevelID,
          roleDescription: newEmployee?.roleDescription ?? "",
          joinDate: formatDate("yyyy-mm-dd", newEmployee?.joinDate) ?? "",
          companyID: newEmployee?.companyID,
          lastDay: formatDate("yyyy-mm-dd", newEmployee?.lastDay) ?? "",
          notes: newEmployee?.notes ?? "",
        });
        break;
      }
      default: {
        setState({
          id: 0,
          fullName: "",
          authRoleID: ERoleId.TEAM_MEMBER,
          userName: "",
          departmentID: userDeptId,
          practiceID: defaultId,
          roleLevelID: defaultId,
          roleDescription: "",
          joinDate: null,
          companyID: defaultId,
          lastDay: null,
          notes: "",
        });
        break;
      }
    }
  }

  const changeStateByKey = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const onHide = () => {
    dispatch(hideModal());
  }

  const isSaveChangesButtonDisabled = () => {
    if (
      loadingModify ||
      !state.fullName ||
      state.authRoleID === defaultId ||
      !state.userName ||
      state.departmentID === defaultId ||
      state.practiceID === defaultId ||
      state.roleLevelID === defaultId ||
      state.companyID === defaultId
    ) {
      return true;
    }
    return false;
  };

  const handleNewEmployeeInput = async (type, value) => {
    switch (type) {
      case ENewEmployeeKey.FULLNAME:
        changeStateByKey(type, value);
        break;
      case ENewEmployeeKey.AUTHROLE_ID:
        changeStateByKey(type, parseInt(value));
        break;
      case ENewEmployeeKey.USERNAME:
        changeStateByKey(type, value);
        break;
      case ENewEmployeeKey.DEPARTMENT_ID:
        changeStateByKey(type, value);
        changeStateByKey(ENewEmployeeKey.PRACTICE_ID, defaultId);
        const newPractices = await dispatch(practiceBusiness.getPracticeByDeptId(value));
        if (newPractices) {
          dispatch(setPractice(newPractices));
        }
        break;
      case ENewEmployeeKey.PRACTICE_ID:
        const practiceId = parseInt(value);
        changeStateByKey(type, practiceId);
        changeStateByKey(ENewEmployeeKey.ROLELEVEL_ID, defaultId);

        const selectedPractice = practices.find(p => p.id === practiceId);
        const isGeneralPractice = selectedPractice && generalPracticesNames.includes(selectedPractice.practiceName);
        let roleLevels;

        if (isGeneralPractice) {
          roleLevels = await dispatch(roleLevelBusiness.getAllRoleLevel());
        }
        else
        {
          roleLevels = await dispatch(roleLevelBusiness.getRoleLevelByPracticeId(practiceId));
        }

        if (roleLevels) {
          dispatch(setRoleLevels(roleLevels));
        } else {
          const allRoleLevels = await dispatch(roleLevelBusiness.getAllRoleLevel());
          dispatch(setRoleLevels(allRoleLevels || []));
        }
        break;
      case ENewEmployeeKey.ROLELEVEL_ID:
        changeStateByKey(type, parseInt(value));
        break;
      case ENewEmployeeKey.ROLE_DESCRIPTION:
        changeStateByKey(type, value);
        break;
      case ENewEmployeeKey.JOIN_DATE:
        changeStateByKey(type, value === "" ? null : value);
        break;
      case ENewEmployeeKey.COMPANY_ID:
        changeStateByKey(type, parseInt(value));
        break;
      case ENewEmployeeKey.LAST_DAY:
        changeStateByKey(type, value === "" ? null : value);
        break;
      case ENewEmployeeKey.NOTES:
        changeStateByKey(type, value);
        break;
      default: 
      break;
    }
  };
    const onSaveChangesClickHandler = async (event) => {
    event.preventDefault();
    setLoadingModify(true);
    switch (modalType) {
      case ModalType.CREATE: {
        await dispatch(addEmployee(state));
        setState(null);
        break;
      }
      case ModalType.UPDATE: {
        await dispatch(updateEmployee(state));
        setState(null);
        break;
      }
      default: 
      break;
    }
    setLoadingModify(false);
  }

  if (!state) {
    return <></>;
  }

   const formField =[
  { key: "fullName", label: "Full Name", type: "text" },
  { key: "userName", label: "Username", type: "text", placeholder: "someone@formulatrix.com" },
  {
    key: "authRoleID",
    label: "Auth Role",
    type: "select",
    options: authRoles?.map((r) => ({ id: r.id, label: r.role })),
    disabled: !isAdmin && !isSuperAdmin
  },
  {
    key: "departmentID",
    label: "Department",
    type: "select",
    options: departments?.map((d) => ({ id: d.id, label: d.departmentName })),
    disabled: !isSuperAdmin
  },
  {
    key: "practiceID",
    label: "Practice",
    type: "select",
    options: practices?.map((p) => ({ id: p.id, label: p.practiceName }))
  },
  {
    key: "roleLevelID",
    label: "Role Level",
    type: "select",
    options: roleLevels?.map((r) => ({ id: r.id, label: r.roleLevelName }))
  },
   { key: "roleDescription", label: "Role Description", type: "text", fullWidth: true },
   { key: "joinDate", label: "Join Date", type: "date" },
   {
     key: "companyID",
     label: "Company",
     type: "select",
     options: companies?.map((c) => ({ id: c.id, label: c.companyName }))
   },
   { key: "lastDay", label: "Last Day", type: "date" },
   { key: "notes", label: "Notes", type: "text", fullWidth: true }
];

  const ModalFooter = () => {
    return (
      <div className='modal-footer'>
        <FmlxButton
          size={FmlxButton.Size.LARGE}
          variant={FmlxButton.Variant.PRIMARY}
          label=
          {
            state.loadingModify ? (
              <div>
                <span
                  style={{ marginLeft: "0px" }}
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              </div>
            ) : (
              "Save Changes"
            )
          }
          disabled={isSaveChangesButtonDisabled()}
          onClick={(e) => onSaveChangesClickHandler(e)}
        />
        <FmlxButton variant={FmlxButton.Variant.CONTAIN} size={FmlxButton.Size.LARGE} type={FmlxButton.Type.DANGER} onClick={onHide} disabled={state.loadingModify} label='cancel' />
      </div>
    );
  }

  return (
    <EssModal
      open={show}
      hideCloseButton
      title={`${modalType === ModalType.CREATE ? "Register" : "View"} Employee `}
      footerButtons={<ModalFooter />}
    >
      <EssFormContent
        state={state}
        fields={formField}
        onChange={handleNewEmployeeInput}
      />
    </EssModal>
  );
};

ModalCreateUpdateEmployee.propTypes = {};

ModalCreateUpdateEmployee.defaultProps = {};

export default memo(ModalCreateUpdateEmployee);